import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Navigation,
  Signpost,
  Truck,
  MessagesSquare,
  PlusCircle,
  UserPlus,
  Bus,
  Route,
  Briefcase,
  Tag,
  Users,
  ClipboardList,
  Package,
  ArrowRight,
  ChevronRight,
  ClipboardCheck,
  Send,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader, Card, TripRoute } from "@/components/common";
import { PushBanner } from "@/components/PushBanner";
import { cn } from "@/lib/utils";

const COMPANY_STATUS = {
  pending: "In attesa di assegnazione",
  available: "Disponibile",
  confirmed: "Assegnato",
  in_progress: "In corso",
  completed: "Completato",
  transferred: "Ceduto",
  cancelled: "Annullato",
};
const MISSION_STATUS = { sent: "Inviato", seen: "Visualizzato", acknowledged: "Ricevuto e confermato" };
const DRIVER_TRIP_STATUS = {
  pending: "In attesa",
  available: "Disponibile",
  confirmed: "Assegnata",
  in_progress: "In corso",
  completed: "Completata",
  transferred: "Ceduta",
  cancelled: "Annullata",
};

function dt(v) {
  return v ? new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
}

function QuickAction({ to, icon: Icon, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex min-h-[94px] flex-col justify-between rounded-2xl border border-border bg-card p-4 transition-transform hover:-translate-y-0.5 hover:shadow-md"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary">
        <Icon className="h-6 w-6 text-primary" />
      </span>
      <span className="text-sm font-bold text-card-foreground">{label}</span>
    </Link>
  );
}

function Hero({ icon: Icon, title, text }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-primary px-5 py-4 text-primary-foreground">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-base font-extrabold">{title}</p>
        <p className="mt-0.5 text-xs text-primary-foreground/80">{text}</p>
      </div>
    </div>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div className="mt-2 flex items-center justify-between">
      <h2 className="text-lg font-extrabold text-foreground">{title}</h2>
      {action || null}
    </div>
  );
}

function StatRow({ stats }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4" data-testid="home-stats">
      {stats.map((s) => (
        <div key={s.label} data-testid={`home-stat-${s.key}`} className="rounded-2xl border border-border bg-card p-3">
          <p className="text-2xl font-extrabold text-primary">{s.value}</p>
          <p className="mt-0.5 text-[11px] font-bold leading-tight text-muted-foreground">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

function TripPreview({ trip }) {
  return (
    <Link to={`/trip/${trip.id}`} data-testid={`home-trip-${trip.id}`} className="block">
      <Card className="space-y-3 hover:shadow-md">
        {trip.compatibility ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-extrabold text-secondary-foreground">
            {trip.compatibility}
          </span>
        ) : null}
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1"><TripRoute trip={trip} /></div>
        </div>
        {trip.price_proposed != null ? (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-xs text-muted-foreground">Prezzo proposto</span>
            <span className="text-base font-extrabold text-primary">€ {trip.price_proposed}</span>
          </div>
        ) : null}
      </Card>
    </Link>
  );
}

// ---------- CARRIER (vettore) HOME ----------
function CarrierHome() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [matches, setMatches] = useState([]);
  const [returnLoads, setReturnLoads] = useState(null);
  const [transports, setTransports] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, v, m, r, tr, u] = await Promise.all([
          apiFetch("/trips"),
          apiFetch("/free-vehicles"),
          apiFetch("/trips/matches").catch(() => []),
          apiFetch("/trips/return-loads").catch(() => null),
          apiFetch("/transports").catch(() => []),
          apiFetch("/notifications/unread-count").catch(() => ({ count: 0 })),
        ]);
        setTrips(t);
        setVehicles(v);
        setMatches(m);
        setReturnLoads(r);
        setTransports(tr);
        setUnread(u?.count ?? 0);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const mine = transports.filter((t) => t.assigned_to === user?.id);
  const stats = [
    { key: "assegnati", label: "Viaggi assegnati", value: mine.filter((t) => t.status === "confirmed").length },
    { key: "missioni", label: "Missioni attive", value: mine.filter((t) => t.status === "in_progress").length },
    { key: "notifiche", label: "Notifiche da leggere", value: unread },
    { key: "consigliati", label: "Viaggi consigliati", value: matches.length },
  ];

  return (
    <div className="space-y-4">
      <p className="text-[11px] font-extrabold tracking-widest text-primary">IL TUO SPAZIO LOGISTICO</p>
      <Hero icon={Navigation} title="Tutto il tuo trasporto, in un posto." text="Trova il carico o il mezzo giusto per la prossima tratta." />
      <StatRow stats={stats} />
      <h2 className="text-lg font-extrabold text-foreground">Azioni rapide</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <QuickAction to="/trips" icon={Signpost} label="Cerca viaggi" />
        <QuickAction to="/free-vehicles" icon={Truck} label="Mezzi liberi" />
        <QuickAction to="/carrier-routes" icon={Route} label="Dove cerco carichi" testId="carrier-action-routes" />
        <QuickAction to="/chats" icon={MessagesSquare} label="Apri chat" />
        <QuickAction to="/publish-trip" icon={PlusCircle} label="Pubblica viaggio" />
        <QuickAction to="/drivers" icon={UserPlus} label="Autisti" />
        <QuickAction to="/fleet" icon={Bus} label="La mia flotta" />
      </div>

      {matches.length > 0 ? (
        <>
          <SectionHeader
            title="Per te"
            action={<Link to="/preferences" className="text-sm font-extrabold text-primary">Le tue tratte</Link>}
          />
          {matches.slice(0, 3).map((t) => <TripPreview key={t.id} trip={t} />)}
        </>
      ) : null}

      {returnLoads && returnLoads.loads.length > 0 ? (
        <>
          <SectionHeader title="Possibili viaggi di ritorno" />
          {returnLoads.current ? (
            <p className="text-xs text-muted-foreground">
              Il tuo mezzo arriva a {returnLoads.current.delivery} ({returnLoads.current.date}): questi carichi partono da
              quella zona o tornano sulle tue tratte.
            </p>
          ) : null}
          {returnLoads.loads.slice(0, 3).map((t) => <TripPreview key={t.id} trip={t} />)}
        </>
      ) : null}

      <SectionHeader title="Viaggi disponibili" action={<Link to="/trips" className="text-sm font-extrabold text-primary">Vedi tutti</Link>} />
      {loading ? <Loader /> : trips.slice(0, 2).map((t) => <TripPreview key={t.id} trip={t} />)}

      <SectionHeader title="Mezzi liberi" action={<Link to="/free-vehicles" className="text-sm font-extrabold text-primary">Vedi tutti</Link>} />
      {vehicles.slice(0, 1).map((v) => (
        <Card key={v.id} className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
            <Truck className="h-5 w-5 text-primary" />
          </span>
          <div className="flex-1">
            <p className="font-extrabold text-foreground">{v.vehicle_type}</p>
            <p className="text-xs text-muted-foreground">{v.location} · {v.available_date}</p>
            <p className="text-xs text-muted-foreground">Destinazione: {v.preferred_destination}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </Card>
      ))}
    </div>
  );
}

// ---------- COMPANY (azienda) HOME ----------
function CompanyHome() {
  const [transports, setTransports] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch("/transports"),
      apiFetch("/carriers").catch(() => []),
      apiFetch("/offers/incoming").catch(() => []),
    ])
      .then(([t, c, o]) => {
        setTransports(t);
        setCarriers(c);
        setOffers(o);
      })
      .finally(() => setLoading(false));
  }, []);

  const active = transports.filter((t) => !["completed", "cancelled", "transferred", "scaduto"].includes(t.status));
  const linkedCount = carriers.filter((c) => c.linked).length;
  const stats = [
    { key: "pubblicati", label: "Viaggi pubblicati", value: transports.filter((t) => t.status !== "draft").length },
    { key: "da-assegnare", label: "Da assegnare", value: transports.filter((t) => ["pending", "available"].includes(t.status)).length },
    { key: "assegnati", label: "Assegnati", value: transports.filter((t) => t.status === "confirmed").length },
    { key: "in-corso", label: "In corso", value: transports.filter((t) => t.status === "in_progress").length },
  ];

  return (
    <div className="space-y-4">
      <p className="text-[11px] font-extrabold tracking-widest text-primary">AREA AZIENDA</p>
      <Hero icon={Briefcase} title="I tuoi trasporti, sotto controllo." text="Pubblica un viaggio e proponilo prima alla tua rete di vettori fidati." />
      <StatRow stats={stats} />
      <h2 className="text-lg font-extrabold text-foreground">Azioni rapide</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <QuickAction testId="company-action-publish" to="/publish-trip" icon={PlusCircle} label="Pubblica viaggio" />
        <QuickAction testId="company-action-carriers" to="/carriers" icon={Users} label="I miei vettori" />
        <QuickAction testId="company-action-vehicles" to="/free-vehicles" icon={Truck} label="Cerca mezzi" />
        <QuickAction testId="company-action-transports" to="/transports" icon={ClipboardList} label="I miei trasporti" />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          {offers.length > 0 ? (
            <>
              <SectionHeader
                title="Proposte ricevute"
                action={
                  <div className="flex items-center gap-2.5">
                    <Link to="/offers-history" className="text-sm font-extrabold text-primary">Storico</Link>
                    <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-2 text-xs font-extrabold text-primary-foreground">
                      {offers.length}
                    </span>
                  </div>
                }
              />
              {offers.slice(0, 3).map((o) => (
                <Link key={o.id} to={`/trip/${o.trip_id}`} data-testid={`company-offer-${o.id}`}>
                  <Card className="flex items-center gap-3 hover:shadow-md">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
                      <Tag className="h-5 w-5 text-primary" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-extrabold text-foreground">{o.carrier_name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {o.pickup} → {o.delivery}{o.date ? ` · ${o.date}` : ""}
                      </p>
                    </div>
                    <span className="text-base font-extrabold text-primary">€ {o.price}</span>
                  </Card>
                </Link>
              ))}
            </>
          ) : null}

          <SectionHeader title="Trasporti attivi" action={<Link to="/transports" className="text-sm font-extrabold text-primary">Vedi tutti</Link>} />
          {active.length === 0 ? (
            <Card className="flex items-center gap-2 border-dashed">
              <Package className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Nessun trasporto attivo. Pubblica il tuo primo viaggio.</span>
            </Card>
          ) : (
            active.slice(0, 3).map((t) => (
              <Link key={t.id} to={`/trip/${t.id}`} data-testid={`company-trip-${t.id}`}>
                <Card className="space-y-2 hover:shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <TripRoute trip={t} />
                      <p className="truncate text-xs text-muted-foreground mt-1">
                        {t.date}{t.time ? ` · ${t.time}` : ""} · {t.vehicle_type}
                      </p>
                    </div>
                    <span
                      className={cn(
                        "rounded-full px-2.5 py-1 text-[11px] font-extrabold",
                        t.status === "confirmed" || t.status === "in_progress"
                          ? "bg-[hsl(var(--success))] text-white"
                          : "bg-secondary text-secondary-foreground"
                      )}
                    >
                      {COMPANY_STATUS[t.status] || t.status}
                    </span>
                  </div>
                  {t.assigned_to_name ? <p className="text-xs text-muted-foreground">Vettore: {t.assigned_to_name}</p> : null}
                </Card>
              </Link>
            ))
          )}

          <SectionHeader title="La tua rete" action={<Link to="/carriers" className="text-sm font-extrabold text-primary">Gestisci</Link>} />
          <Link to="/carriers" data-testid="company-network-card">
            <Card className="flex items-center gap-3 hover:shadow-md">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
                <Users className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-foreground">{linkedCount} vettori collegati</p>
                <p className="text-xs text-muted-foreground">
                  {carriers.length === 0
                    ? "Associa i vettori con cui lavori per proporre loro viaggi riservati."
                    : `${carriers.length} nella tua rubrica · i viaggi riservati arrivano prima a loro.`}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </Card>
          </Link>
        </>
      )}
    </div>
  );
}

// ---------- DRIVER (autista) HOME ----------
export function DriverHome() {
  const [missions, setMissions] = useState([]);
  const [directMissions, setDirectMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ackLoading, setAckLoading] = useState("");

  const load = () =>
    Promise.all([apiFetch("/transports"), apiFetch("/missions").catch(() => [])])
      .then(([t, d]) => {
        setMissions(t);
        setDirectMissions(d);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const acknowledge = async (m) => {
    setAckLoading(m.id);
    try {
      const updated = await apiFetch(`/missions/${m.id}/ack`, { method: "POST", body: "{}" });
      setDirectMissions((old) => old.map((it) => (it.id === m.id ? updated : it)));
    } finally {
      setAckLoading("");
    }
  };

  const activeMissions = missions.filter((m) => !["completed", "cancelled", "transferred", "scaduto"].includes(m.status));

  return (
    <div className="space-y-3">
      <p className="text-[11px] font-extrabold tracking-widest text-primary">AREA AUTISTA</p>
      <p className="text-[15px] leading-relaxed text-muted-foreground">
        Vedi solo i viaggi assegnati dal tuo vettore, con carico, scarico, documenti e stato consegna.
      </p>
      {loading ? (
        <Loader />
      ) : activeMissions.length === 0 && directMissions.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <ClipboardList className="h-10 w-10 text-muted-foreground" />
          <p className="text-lg font-extrabold text-foreground">Nessuna missione assegnata</p>
          <p className="max-w-xs text-sm text-muted-foreground">Quando il tuo vettore ti assegna un viaggio, lo troverai qui.</p>
        </div>
      ) : (
        <>
          {directMissions.map((m) => (
            <Card key={`direct-${m.id}`} data-testid={`driver-direct-mission-${m.id}`} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p className="flex-1 font-extrabold text-foreground">{m.pickup_address}</p>
                <ArrowRight className="h-5 w-5 text-primary" />
                <p className="flex-1 text-right font-extrabold text-foreground">{m.delivery_address}</p>
              </div>
              <p className="text-[13px] text-muted-foreground">Data · {m.date}{m.time ? ` · ${m.time}` : ""}</p>
              {m.customer ? <p className="text-[13px] text-muted-foreground">Cliente/stabilimento · {m.customer}</p> : null}
              {m.reference ? <p className="text-[13px] text-muted-foreground">Riferimento · {m.reference}</p> : null}
              {m.tractor_plate || m.trailer_plate ? (
                <p className="text-[13px] text-muted-foreground">Targhe · {[m.tractor_plate, m.trailer_plate].filter(Boolean).join(" / ")}</p>
              ) : null}
              {m.description ? <p className="text-[13px] text-muted-foreground">{m.description}</p> : null}
              {m.notes ? <p className="text-[13px] text-muted-foreground">Note · {m.notes}</p> : null}
              <div className="flex items-center justify-between pt-2">
                <span
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[11px] font-extrabold",
                    m.status === "acknowledged" ? "bg-[hsl(var(--success))] text-white" : "bg-secondary text-secondary-foreground"
                  )}
                >
                  {MISSION_STATUS[m.status]}
                </span>
                <span className="text-xs font-bold text-muted-foreground">
                  {m.status === "acknowledged" ? dt(m.acknowledged_at) : m.status === "seen" ? dt(m.seen_at) : dt(m.sent_at)}
                </span>
              </div>
              {m.status !== "acknowledged" ? (
                <button
                  data-testid={`driver-ack-${m.id}`}
                  onClick={() => acknowledge(m)}
                  disabled={ackLoading === m.id}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-secondary/60 py-2.5 text-xs font-extrabold text-secondary-foreground"
                >
                  {ackLoading === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 text-primary" />}
                  Confermo ricezione
                </button>
              ) : null}
            </Card>
          ))}
          {activeMissions.map((m) => (
            <Card key={m.id} data-testid={`driver-mission-${m.id}`} className="space-y-2">
              <Link to={`/trip/${m.id}`} className="block space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="flex-1 font-extrabold text-foreground">{m.pickup}</p>
                  <ArrowRight className="h-5 w-5 text-primary" />
                  <p className="flex-1 text-right font-extrabold text-foreground">{m.delivery}</p>
                </div>
                <p className="text-[13px] text-muted-foreground">Carico · {m.date}{m.time ? ` · ${m.time}` : ""}</p>
                <p className="text-[13px] text-muted-foreground">{m.cargo || "Trasporto merci"} · {m.vehicle_type}</p>
                <div className="flex items-center justify-between pt-1">
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-1 text-[11px] font-extrabold",
                      m.status === "completed" ? "bg-[hsl(var(--success))] text-white" : "bg-secondary text-secondary-foreground"
                    )}
                  >
                    {DRIVER_TRIP_STATUS[m.status] || m.status}
                  </span>
                  {m.assigned_to_name ? <span className="text-xs font-bold text-muted-foreground">{m.assigned_to_name}</span> : null}
                </div>
              </Link>
              <div className="flex gap-2 border-t border-border pt-2">
                <Link to={`/trip/${m.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border bg-secondary/60 py-2.5 text-xs font-extrabold text-secondary-foreground">
                  <Navigation className="h-4 w-4 text-primary" /> Dettagli e tratta
                </Link>
              </div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  if (!user) return <Loader />;
  let content;
  if (user.account_type === "autista") content = <DriverHome />;
  else if (user.account_type === "azienda") content = <CompanyHome />;
  else content = <CarrierHome />;
  return (
    <>
      <PushBanner />
      {content}
    </>
  );
}
